import { SubmenuDivHandler } from './SubmenuDivHandler.js';
import { EventsClient } from '../events-client.js';
import Logger from '../utils/logger.js';
import { UserMessaging } from '../utils/user-messaging.js';
import {
    createEntertainersMenuSchema,
    ENTERTAINERS_MENU_ACTIONS,
    ENTERTAINERS_MENU_LABELS,
    readEntertainersSchemaButtonLabel,
    readEntertainersSchemaInputPlaceholder,
} from './schemas/entertainers-menu-schema.js';

const MODULE = 'EntertainersSubmenu';

const CSS = `
        .mm-entertainers-grid {
            display: grid;
            gap: 0.75rem;
            grid-template-columns: 1fr;
            margin-top: 0.5rem;
        }

        .mm-entertainers-card {
            border: 1px solid #00d9ff;
            border-radius: 10px;
            padding: 0.75rem;
            background: rgba(10, 15, 30, 0.45);
            text-align: left;
        }

        .mm-entertainers-card h3 {
            margin: 0 0 0.55rem 0;
            color: #b8e3f0;
            font-size: clamp(0.85rem, 2.3vw, 1rem);
            text-transform: uppercase;
            letter-spacing: 0.06em;
        }

        .mm-entertainers-row {
            display: flex;
            flex-direction: column;
            gap: 0.3rem;
            margin-bottom: 0.6rem;
        }

        .mm-entertainers-row label {
            color: #b8e3f0;
            font-size: clamp(0.78rem, 2vw, 0.88rem);
            font-weight: 600;
        }

        .mm-entertainers-readonly {
            color: #00ffff;
            font-size: clamp(0.85rem, 2.3vw, 1rem);
            font-weight: 700;
            padding: 0.35rem 0;
        }

        .mm-entertainers-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.4rem;
        }

        .mm-entertainers-list {
            border: 1px solid rgba(0, 217, 255, 0.35);
            border-radius: 8px;
            max-height: 300px;
            overflow-y: auto;
        }

        .mm-entertainers-list-row {
            padding: 0.45rem 0.6rem;
            border-bottom: 1px solid rgba(0, 217, 255, 0.15);
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 0.5rem;
        }

        .mm-entertainers-list-row:last-child {
            border-bottom: none;
        }

        .mm-entertainers-list-row:hover {
            background: rgba(0, 217, 255, 0.08);
        }

        .mm-entertainers-list-row.selected {
            background: rgba(0, 217, 255, 0.18);
        }

        .mm-entertainers-username {
            color: #00ffff;
            font-weight: 700;
            font-size: clamp(0.78rem, 2vw, 0.88rem);
        }

        .mm-entertainers-displayname {
            color: #e0f4ff;
            font-size: clamp(0.74rem, 1.9vw, 0.84rem);
            opacity: 0.9;
        }

        .mm-entertainers-status {
            font-size: clamp(0.78rem, 2vw, 0.88rem);
            font-weight: 600;
            min-height: 1.2em;
            margin-top: 0.35rem;
        }

        .mm-entertainers-status.success {
            color: #00ff9d;
            text-shadow: 0 0 6px rgba(0, 255, 157, 0.5);
        }

        .mm-entertainers-status.error {
            color: #ff4444;
            text-shadow: 0 0 6px rgba(255, 68, 68, 0.5);
        }

        @media (min-width: 960px) {
            .mm-entertainers-grid {
                grid-template-columns: 1fr 1fr;
            }
        }
`;

const MM_ENTERTAINERS_HTML = `
        <div id="menu-modal-entertainers" class="menu-modal-content">
            <h2>Entertainers</h2>
            <div class="mm-entertainers-grid">
                <div class="mm-entertainers-card">
                    <h3>Send Tip</h3>
                    <div class="mm-entertainers-row">
                        <label>Your Token Balance</label>
                        <div id="mm-entertainers-my-balance" class="mm-entertainers-readonly">0</div>
                    </div>
                    <div class="mm-entertainers-row">
                        <label id="mm-entertainers-selected-label">Selected Entertainer</label>
                        <div id="mm-entertainers-selected" class="mm-entertainers-readonly">-</div>
                    </div>
                    <div class="mm-entertainers-row">
                        <label for="mm-entertainers-tip-amount">Tip Amount</label>
                        <input id="mm-entertainers-tip-amount" class="mm-text-input" type="number" min="1"
                            placeholder="tokens to tip" onkeydown="event.stopPropagation()" />
                    </div>
                    <div class="mm-entertainers-actions">
                        <button id="mm-entertainers-tip-button" class="mm-submit-button" type="button" disabled>Send Tip</button>
                        <button id="mm-entertainers-refresh-button" class="mm-submit-button" type="button">Refresh</button>
                    </div>
                    <div id="mm-entertainers-status" class="mm-entertainers-status"></div>
                </div>

                <div class="mm-entertainers-card">
                    <h3>Entertainers In Room</h3>
                    <div id="mm-entertainers-room-list" class="mm-entertainers-list"></div>
                </div>
            </div>
        </div>
`;

interface EntertainerSummary {
    id: string;
    username: string;
    display_name: string | null;
}

interface MarketplaceEntertainersResponse {
    room: string;
    my_token_balance: number;
    entertainers: EntertainerSummary[];
}

export class EntertainersSubmenuDH extends SubmenuDivHandler {
    static TypeName = 'EntertainersSubmenuDH';

    private styleEl: HTMLStyleElement | null = null;
    private selectedEntertainerId: string | null = null;
    private entertainers: EntertainerSummary[] = [];
    private myBalance = 0;

    updateConditionals(): void {
        // No periodic refresh needed.
    }

    displayMenuContent(containerDiv: HTMLDivElement): void {
        this.styleEl = document.createElement('style');
        this.styleEl.innerText = CSS;
        document.head.appendChild(this.styleEl);

        containerDiv.innerHTML = MM_ENTERTAINERS_HTML;
        const schema = createEntertainersMenuSchema();
        this.applySchemaLabels(containerDiv, schema);

        const eventsClient = EventsClient.instance;
        if (!eventsClient) {
            Logger.error(MODULE, 'EventsClient singleton not available.');
            return;
        }

        const token = eventsClient.getAuthToken();
        if (!token) {
            this.setStatus(ENTERTAINERS_MENU_LABELS.notAuthenticated, 'error');
            return;
        }

        const tipButton = document.getElementById('mm-entertainers-tip-button') as HTMLButtonElement | null;
        const refreshButton = document.getElementById('mm-entertainers-refresh-button') as HTMLButtonElement | null;
        const tipAmountInput = document.getElementById('mm-entertainers-tip-amount') as HTMLInputElement | null;

        tipButton?.addEventListener('click', () => {
            void this.sendTip(eventsClient, token);
        });
        refreshButton?.addEventListener('click', () => {
            void this.loadEntertainers(eventsClient, token);
        });
        tipAmountInput?.addEventListener('input', () => {
            this.updateTipButtonEnabled();
        });

        void this.loadEntertainers(eventsClient, token);
    }

    hideContent(): void {
        if (this.styleEl && this.styleEl.parentNode) {
            this.styleEl.parentNode.removeChild(this.styleEl);
            this.styleEl = null;
        }
        this.selectedEntertainerId = null;
        this.entertainers = [];
        this.myBalance = 0;
    }

    private applySchemaLabels(containerDiv: HTMLDivElement, schema: ReturnType<typeof createEntertainersMenuSchema>): void {
        const titleEl = containerDiv.querySelector('h2');
        if (titleEl) {
            titleEl.textContent = schema.title;
        }

        const tipAmountLabel = containerDiv.querySelector('label[for="mm-entertainers-tip-amount"]');
        if (tipAmountLabel) {
            tipAmountLabel.textContent = ENTERTAINERS_MENU_LABELS.tipAmount;
        }

        const tipAmountInput = document.getElementById('mm-entertainers-tip-amount') as HTMLInputElement | null;
        if (tipAmountInput) {
            tipAmountInput.placeholder = readEntertainersSchemaInputPlaceholder(
                schema,
                'entertainers-tip-amount-input',
                ENTERTAINERS_MENU_LABELS.tipAmountPlaceholder,
            );
        }

        const tipButton = document.getElementById('mm-entertainers-tip-button') as HTMLButtonElement | null;
        if (tipButton) {
            tipButton.textContent = readEntertainersSchemaButtonLabel(
                schema,
                ENTERTAINERS_MENU_ACTIONS.sendTip,
                ENTERTAINERS_MENU_LABELS.sendTip,
            );
        }

        const refreshButton = document.getElementById('mm-entertainers-refresh-button') as HTMLButtonElement | null;
        if (refreshButton) {
            refreshButton.textContent = readEntertainersSchemaButtonLabel(
                schema,
                ENTERTAINERS_MENU_ACTIONS.refreshList,
                ENTERTAINERS_MENU_LABELS.refreshList,
            );
        }
    }

    private async loadEntertainers(eventsClient: EventsClient, token: string): Promise<void> {
        const listEl = document.getElementById('mm-entertainers-room-list');
        if (listEl) {
            listEl.innerHTML = '<div class="mm-entertainers-list-row">Loading entertainers...</div>';
        }

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/marketplace/entertainers`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                const msg = (data as { error?: string }).error ?? 'Failed to load entertainers.';
                this.setStatus(msg, 'error');
                if (listEl) {
                    listEl.innerHTML = `<div class="mm-entertainers-list-row">${msg}</div>`;
                }
                return;
            }

            const payload = data as MarketplaceEntertainersResponse;
            this.myBalance = Number(payload.my_token_balance ?? 0);
            this.entertainers = Array.isArray(payload.entertainers) ? payload.entertainers : [];

            if (this.selectedEntertainerId && !this.entertainers.some((row) => row.id === this.selectedEntertainerId)) {
                this.selectedEntertainerId = null;
            }

            this.renderEntertainerList();
            this.updateBalanceLabels();
            this.updateTipButtonEnabled();
            this.setStatus('', '');
        } catch (err) {
            Logger.error(MODULE, 'Error loading entertainers', err);
            this.setStatus('Error loading entertainers.', 'error');
            if (listEl) {
                listEl.innerHTML = '<div class="mm-entertainers-list-row">Error loading entertainers.</div>';
            }
        }
    }

    private renderEntertainerList(): void {
        const listEl = document.getElementById('mm-entertainers-room-list');
        if (!listEl) {
            return;
        }

        if (this.entertainers.length === 0) {
            listEl.innerHTML = `<div class="mm-entertainers-list-row">${ENTERTAINERS_MENU_LABELS.noEntertainers}</div>`;
            return;
        }

        listEl.innerHTML = '';
        for (const entertainer of this.entertainers) {
            const row = document.createElement('div');
            row.className = 'mm-entertainers-list-row';
            row.dataset['id'] = entertainer.id;

            const left = document.createElement('div');
            const userName = document.createElement('div');
            userName.className = 'mm-entertainers-username';
            userName.textContent = entertainer.username;
            const displayName = document.createElement('div');
            displayName.className = 'mm-entertainers-displayname';
            displayName.textContent = entertainer.display_name ?? '—';
            left.appendChild(userName);
            left.appendChild(displayName);

            row.appendChild(left);

            row.addEventListener('click', () => {
                this.selectedEntertainerId = entertainer.id;
                this.highlightSelectedRow(entertainer.id);
                this.updateBalanceLabels();
                this.updateTipButtonEnabled();
                this.setStatus(`Selected entertainer: ${entertainer.display_name || entertainer.username}`, 'success');
            });

            listEl.appendChild(row);
        }

        if (this.selectedEntertainerId) {
            this.highlightSelectedRow(this.selectedEntertainerId);
        }
    }

    private highlightSelectedRow(entertainerId: string): void {
        const listEl = document.getElementById('mm-entertainers-room-list');
        if (!listEl) {
            return;
        }

        for (const row of Array.from(listEl.querySelectorAll('.mm-entertainers-list-row'))) {
            row.classList.remove('selected');
            if ((row as HTMLElement).dataset['id'] === entertainerId) {
                row.classList.add('selected');
            }
        }
    }

    private updateBalanceLabels(): void {
        const myBalanceEl = document.getElementById('mm-entertainers-my-balance');
        if (myBalanceEl) {
            myBalanceEl.textContent = String(this.myBalance);
        }

        const selected = this.entertainers.find((row) => row.id === this.selectedEntertainerId);
        const selectedEl = document.getElementById('mm-entertainers-selected');
        if (selectedEl) {
            selectedEl.textContent = selected
                ? `${selected.display_name || selected.username}`
                : '-';
        }
    }

    private updateTipButtonEnabled(): void {
        const tipButton = document.getElementById('mm-entertainers-tip-button') as HTMLButtonElement | null;
        const amountInput = document.getElementById('mm-entertainers-tip-amount') as HTMLInputElement | null;
        if (!tipButton || !amountInput) {
            return;
        }

        const amount = Number(amountInput.value || '0');
        const valid = !!this.selectedEntertainerId
            && Number.isInteger(amount)
            && amount > 0
            && amount <= this.myBalance;

        tipButton.disabled = !valid;
    }

    private async sendTip(eventsClient: EventsClient, token: string): Promise<void> {
        if (!this.selectedEntertainerId) {
            this.setStatus(ENTERTAINERS_MENU_LABELS.selectEntertainer, 'error');
            return;
        }

        const amountInput = document.getElementById('mm-entertainers-tip-amount') as HTMLInputElement | null;
        if (!amountInput) {
            return;
        }

        const amount = Number(amountInput.value || '0');
        if (!Number.isInteger(amount) || amount <= 0) {
            this.setStatus('Tip amount must be a positive whole number.', 'error');
            return;
        }

        if (amount > this.myBalance) {
            this.setStatus('Tip amount exceeds your token balance.', 'error');
            return;
        }

        this.setStatus('', '');

        try {
            const resp = await fetch(`${eventsClient.getRestApiBaseUrl()}/api/marketplace/tips`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    entertainer_user_id: this.selectedEntertainerId,
                    amount,
                }),
            });

            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                const msg = (data as { error?: string }).error ?? 'Tip failed.';
                this.setStatus(msg, 'error');
                UserMessaging.instance.notify(`Tip failed: ${msg}`, 6.0);
                return;
            }

            const payload = data as {
                message?: string;
                sender?: { balance_after?: number };
                recipient?: { id?: string };
            };

            this.myBalance = Number(payload.sender?.balance_after ?? this.myBalance - amount);

            amountInput.value = '';
            this.updateBalanceLabels();
            this.renderEntertainerList();
            this.updateTipButtonEnabled();

            const successMessage = payload.message || `Tip sent (${amount} token${amount === 1 ? '' : 's'}).`;
            this.setStatus(successMessage, 'success');
            UserMessaging.instance.notify(successMessage, 6.0);
        } catch (err) {
            Logger.error(MODULE, 'Error sending tip', err);
            this.setStatus('Error sending tip.', 'error');
        }
    }

    private setStatus(message: string, type: '' | 'success' | 'error'): void {
        const statusEl = document.getElementById('mm-entertainers-status');
        if (!statusEl) {
            return;
        }
        statusEl.textContent = message;
        statusEl.className = `mm-entertainers-status${type ? ` ${type}` : ''}`;
    }
}
